import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { ApiError } from '@/lib/apiErrors';
import type { ApplicationResponse } from '@/types/generated/openapi';

/** The API's answer when someone else saved first; other 409s (stock, constraints) say their own reason. */
export const LOCK_MESSAGE = /changed by someone else/i;

export interface ApplicationChanges {
  application_date?: string;
  product_id?: string;
  amount?: number;
  reason?: string | null;
  notes?: string | null;
}

/**
 * Edit and delete for one application, shared by the web dialog and the phone editor.
 * Everything that derives from application rows is refreshed: the journal and totals, the
 * entry screen's day list, product use counts and the entry hub badges.
 */
export function useApplicationActions(farmId: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
  };

  const update = useMutation({
    // A PUT carrying the last_modified the editor saw can't be retried silently after a 409.
    retry: false,
    mutationFn: ({ row, changes }: { row: ApplicationResponse; changes: ApplicationChanges }) =>
      api.put(`/api/v1/farms/${farmId}/applications/${row.id}`, {
        ...changes,
        expected_last_modified: row.last_modified,
      }),
    onSuccess: refresh,
    onError: (err) => {
      // Someone else's change (or a rule we mirrored wrongly): show their version next.
      if (err instanceof ApiError && ((err.status === 409 && LOCK_MESSAGE.test(err.message)) || err.status === 404)) refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (row: ApplicationResponse) => api.delete(`/api/v1/farms/${farmId}/applications/${row.id}`),
    onSuccess: refresh,
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404) refresh();
    },
  });

  return { update, remove };
}
